import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import ShortlistCardMenu from './ShortlistCardMenu';
import { ShortlistMosaic, MosaicTile } from './ShortlistMosaic';
import { cldUrl } from '../../../utils/cloudinary';
import { initialsFromName } from '../../../utils';

interface Shortlist {
  id: number;
  name: string;
  deactivated: boolean;
  created_at: string;
  updated_at: string;
  experts: any[];
}

interface Props {
  shortlist: Shortlist;
  fetchShortlists: any;
}

const ShortlistCard = ({ shortlist, fetchShortlists }: Props) => {
  const navigate = useNavigate();

  const experts = useMemo(() => shortlist?.experts || [], [shortlist]);
  const tiles = useMemo<MosaicTile[]>(
    () =>
      experts.slice(0, 8).map((e: any, i: number) => ({
        key: e.userid ?? i,
        src: e.avatar
          ? cldUrl(e.avatar, { w: 400, h: 400, crop: 'fill', gravity: 'face' })
          : undefined,
        fallback: (
          <span className="font-bold text-accent-text" style={{ fontSize: 15.5 }}>
            {initialsFromName(e.displayName || '')}
          </span>
        ),
      })),
    [experts]
  );

  if (!shortlist) return null;

  const open = () => navigate(`/experts/shortlists/${shortlist.id}?shortlist=${shortlist.id}`);

  return (
    <>
      <div
        className="bg-surface border border-border-token rounded-xl overflow-hidden flex flex-col h-full w-full box-border cursor-pointer transition-colors duration-150 hover:border-accent"
        style={{ boxShadow: 'var(--sh-1)' }}
        onClick={open}
        title={shortlist.name}
      >
        <ShortlistMosaic
          tiles={tiles}
          total={experts.length}
          countLabel={`${experts.length} ${experts.length === 1 ? 'expert' : 'experts'}`}
          emptyTitle="No experts yet"
          emptyAction={{ label: 'Add from Experts', onClick: () => navigate('/experts') }}
        />

        <div className="flex flex-col flex-1 min-h-0" style={{ gap: 12, padding: '13px 15px 15px' }}>
          <div className="flex items-start gap-2.5">
            <div className="flex-1 min-w-0">
            <div
              className="font-bold text-text-1"
              style={{
                fontSize: 15.5,
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {shortlist.name}
            </div>
            </div>
            <span className="flex items-center shrink-0" onClick={(e) => e.stopPropagation()}>
              <ShortlistCardMenu shortlist={shortlist} fetchShortlists={fetchShortlists} />
            </span>
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t border-border-token mt-auto"
            style={{ paddingTop: 11 }}
          >
            {shortlist.updated_at && (
              <span className="text-text-2 shrink-0" style={{ fontSize: 12.5 }}>
                {moment(shortlist.updated_at).fromNow()}
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ShortlistCard;
